/**
 * SanPlumb — Modelo conceptual (CLAUDE.md §IV)
 *
 * Estos tipos codifican los INVARIANTES §IV.1–7 directamente en el sistema
 * de tipos de TypeScript. Esto significa que el compilador rechaza, antes
 * de runtime, construcciones como `Pipe{system:'AF', mat:'CPVC'}` (que
 * violarían §IV.6).
 *
 * Reglas codificadas:
 *  §IV.1  Todo Pipe tiene `system` obligatorio          → discriminante
 *  §IV.2  Todo Accessory tiene `system` obligatorio     → discriminante
 *  §IV.3  pipes solo AF, pipes_ac solo AC               → AFPipe[] / ACPipe[]
 *  §IV.4  allPipes = concat (ver helper abajo)          → función pura
 *  §IV.6  Pipe AF nunca con CPVC                        → AFPipe.mat = 'PVC-P'
 *  §IV.7  Pipe AC nunca con PVC-P                       → ACPipe.mat = 'CPVC'
 */

// ──────────────────────────────────────────────────────────────────
// Primitivos
// ──────────────────────────────────────────────────────────────────

/** Coordenada 3D en metros, ejes [x, y, z]. Origen: esquina inferior izquierda del cuarto. */
export type Vec3 = readonly [number, number, number];

/** Diámetros normados IS.010 soportados en MVP. */
export type Diameter = '1/2"' | '3/4"' | '1"' | '1¼"';

/** Sistema hidráulico — discriminante en Pipe y Accessory. */
export type System = 'AF' | 'AC';

// ──────────────────────────────────────────────────────────────────
// Room
// ──────────────────────────────────────────────────────────────────

export interface Room {
  /** Ancho libre interior en metros. */
  w: number;
  /** Profundidad libre interior en metros. */
  d: number;
  /** Altura interior en metros. */
  h: number;
  /** Ancho del vano de puerta en metros. Default sugerido: 0.90. */
  doorW: number;
  /** Altura del vano de puerta en metros. Default sugerido: 2.10. */
  doorH: number;
  /**
   * Espesor del muro en metros. Default: 0.15 (ladrillo King Kong peruano
   * 13 cm + tarrajeo ~1 cm por cara). Usado por el visor 3D para representar
   * el espesor real y por el engine de routing para decidir si la Estrategia A
   * (muro dual) es viable.
   */
  murW: number;
  /**
   * Espesor del contrapiso (acabado + falso piso) en metros, debajo del
   * NPT ±0.00. Default: 0.05 (5 cm). La losa estructural queda en
   * y = −contrapiso. Necesario para el plano "NPT losa de piso" del visor.
   */
  contrapiso: number;
}

// ──────────────────────────────────────────────────────────────────
// Pipe — discriminated union (§IV.1, §IV.6, §IV.7)
// ──────────────────────────────────────────────────────────────────

interface PipeBase {
  id: string;
  label: string;
  start: Vec3;
  end: Vec3;
  diam: Diameter;
  /** Longitud en metros (calculada o declarada). */
  len: number;
}

/** Tubería de agua fría — material forzado a PVC-P por §IV.6. */
export interface AFPipe extends PipeBase {
  system: 'AF';
  mat: 'PVC-P';
}

/** Tubería de agua caliente — material forzado a CPVC por §IV.7. */
export interface ACPipe extends PipeBase {
  system: 'AC';
  mat: 'CPVC';
}

export type Pipe = AFPipe | ACPipe;

// ──────────────────────────────────────────────────────────────────
// Accessory — discriminated union (§IV.2)
// ──────────────────────────────────────────────────────────────────

/** Códigos de color semánticos para accesorios (§V). */
export type AccessoryColor =
  | 'acc' //          accesorios AF       → var(--c3)
  | 'acc_ac' //       accesorios AC       → var(--c4)
  | 'valve' //        válvula AF          → var(--c5)
  | 'valve_ac' //     válvula AC          → var(--c5)
  | 'reduction' //    reducción AF        → var(--red)
  | 'reduction_ac' // reducción AC        → var(--red)
  | 'llave_af' //     ensamblaje llave AF → §VII
  | 'llave_ac'; //    ensamblaje llave AC → §VII

interface AccessoryBase {
  id: string;
  /** Tipo: 'Codo 90°', 'Tee', 'Tee reductora', 'Unión Universal', etc. */
  type: string;
  /** Descripción completa para el BOM. */
  spec: string;
  pos: Vec3;
  color: AccessoryColor;
  /**
   * §VII — Marca el accesorio como ensamblaje completo de llave de paso.
   * Cuando es true:
   *  - el visor 3D lo renderiza con mkLlavePaso() (ensamblaje en U)
   *  - el BOM lo lista como UNA línea agregada (no codos/UU/niples sueltos)
   *  - el catálogo permite seleccionar el tipo de válvula (compuerta/esférica/globo)
   */
  isSwitchboard?: boolean;
}

export interface AFAccessory extends AccessoryBase {
  system: 'AF';
}

export interface ACAccessory extends AccessoryBase {
  system: 'AC';
}

export type Accessory = AFAccessory | ACAccessory;

// ──────────────────────────────────────────────────────────────────
// Sanitary — aparatos sanitarios
// ──────────────────────────────────────────────────────────────────

/** Tipos de aparatos sanitarios soportados en MVP (§VI, §VIII, §IX). */
export type SanitaryType =
  | 'Inodoro'
  | 'Lavatorio'
  | 'Ducha'
  | 'Urinario'
  | 'Lavadero de ropa'
  | 'Lavadero de platos';

/**
 * Sanitary no se discrimina por sistema porque un mismo aparato puede
 * tener entradas AF y AC (lavatorio, ducha, lavaderos). La presencia de
 * `has_ac` modula la existencia de `conn_ac` y `ug_ac`.
 */
export interface Sanitary {
  id: string;
  type: SanitaryType;
  /** Subtipo específico de catálogo (ej. 'Inodoro c/tanque', 'Inodoro válvula semiaut.'). */
  subtype: string;
  pos: Vec3;
  /** Dimensiones físicas del aparato en metros (§IX). */
  w: number;
  d: number;
  h: number;
  /** Especificación del punto AF en muro/piso. */
  conn_af: string;
  /** Especificación del punto AC. Definida solo si has_ac. */
  conn_ac?: string;
  has_ac: boolean;
  /** Marca del fabricante (Trebol, Vainsa, Edesa, etc. §X). */
  brand: string;
  /** Unidades Hunter de Gasto AF — IS.010 Anexo 1/2 (§VIII). */
  ug_af: number;
  /** Unidades Hunter de Gasto AC — definidas solo si has_ac. */
  ug_ac?: number;
}

// ──────────────────────────────────────────────────────────────────
// SystemData — estado completo (§IV.3, §IV.4)
// ──────────────────────────────────────────────────────────────────

/**
 * Estado completo del sistema sanitario en diseño.
 *
 * INVARIANTES garantizados por el tipo:
 *  §IV.3  pipes contiene solo AF (AFPipe[]), pipes_ac solo AC (ACPipe[])
 *  §IV.4  allPipes() = [...pipes, ...pipes_ac]
 */
export interface SystemData {
  room: Room;
  pipes: AFPipe[];
  pipes_ac: ACPipe[];
  accessories: AFAccessory[];
  accessories_ac: ACAccessory[];
  sanitaries: Sanitary[];
  /**
   * Estrategia de ruteo activa para la visualización 3D actual.
   * - 'muro_dual': AF + AC en el mismo muro, mismo plano horizontal,
   *   separación ≥ 3 cm (IS.010 §2.3.i). Default normativo.
   * - 'mixta': AF en muro (y=+0.30 m) + AC por contrapiso (y=+0.05 m).
   * - 'piso_dual': AF + AC ambas por contrapiso.
   */
  routingStrategy: RoutingStrategy;
  /**
   * Origen del routing actual:
   * - 'user_defined': el usuario subió un plano con tuberías y cotas; las pipes
   *   reflejan EXACTAMENTE ese diseño. Regenerar requiere confirmación porque
   *   reemplaza el routing personalizado.
   * - 'auto_generated': el engine generó el routing automáticamente a partir
   *   de los sanitarios. Regenerar rota libremente entre estrategias A/B/C.
   */
  routingSource: RoutingSource;
}

// ──────────────────────────────────────────────────────────────────
// Routing — Estrategias y origen
// ──────────────────────────────────────────────────────────────────

export type RoutingStrategy = 'muro_dual' | 'mixta' | 'piso_dual';

export type RoutingSource = 'user_defined' | 'auto_generated';

// ──────────────────────────────────────────────────────────────────
// Helpers (equivalentes a los getters allPipes / allAccessories §IV)
// ──────────────────────────────────────────────────────────────────

export const allPipes = (s: SystemData): Pipe[] => [...s.pipes, ...s.pipes_ac];

export const allAccessories = (s: SystemData): Accessory[] => [
  ...s.accessories,
  ...s.accessories_ac,
];
